import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/lib/company";

// Production canonical — falls back to runtime origin in dev/preview.
const SITE_URL =
    (typeof window !== "undefined" && window.location.origin) ||
    "https://claimhubuk.co.uk";

const DEFAULT_DESCRIPTION =
    "ClaimHub UK — the trusted hub for UK claims. PCP & motor finance, housing disrepair, personal injury, medical negligence and 7 more sectors. Free check, No Win No Fee.";

const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export default function Seo({
    title,
    description = DEFAULT_DESCRIPTION,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    type = "website",
    jsonLd,
    noIndex = false,
}) {
    const baseTitle = "ClaimHub UK — UK Claims Specialists";
    const fullTitle = title ? `${title} · ClaimHub UK` : baseTitle;
    const url = `${SITE_URL}${path}`;
    const jsonLdArray = jsonLd
        ? Array.isArray(jsonLd) ? jsonLd : [jsonLd]
        : [];
    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {noIndex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph */}
            <meta property="og:site_name" content="ClaimHub UK" />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:alt" content="ClaimHub UK — UK Claims Specialists. No Win, No Fee." />
            <meta property="og:locale" content="en_GB" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {/* Theme */}
            <meta name="theme-color" content="#0a192f" />

            {jsonLdArray.map((obj, i) => (
                <script key={i} type="application/ld+json">
                    {JSON.stringify(obj)}
                </script>
            ))}
        </Helmet>
    );
}

// ---------- Schema.org helpers ----------

export function organizationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: COMPANY.legalName,
        alternateName: COMPANY.name,
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        email: COMPANY.email,
        telephone: COMPANY.phoneTel,
        address: {
            "@type": "PostalAddress",
            streetAddress: COMPANY.address.line1,
            addressLocality: `${COMPANY.address.line2}, ${COMPANY.address.city}`,
            postalCode: COMPANY.address.postcode,
            addressCountry: "GB",
        },
        contactPoint: [
            {
                "@type": "ContactPoint",
                telephone: COMPANY.phoneTel,
                contactType: "customer service",
                email: COMPANY.email,
                areaServed: "GB",
                availableLanguage: ["en-GB"],
            },
        ],
        sameAs: [],
    };
}

export function legalServiceSchema(sector) {
    return {
        "@context": "https://schema.org",
        "@type": "LegalService",
        name: `${sector.name} Claims · ClaimHub UK`,
        description: sector.intro,
        url: `${SITE_URL}/sectors/${sector.slug}`,
        provider: { "@id": `${SITE_URL}/#organization` },
        areaServed: { "@type": "Country", name: "United Kingdom" },
        priceRange: sector.averagePayout,
        offers: {
            "@type": "Offer",
            description: `Free eligibility check for ${sector.name} claims — No Win, No Fee.`,
            price: "0",
            priceCurrency: "GBP",
        },
    };
}

export function breadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: `${SITE_URL}${it.path}`,
        })),
    };
}

export function faqPageSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
    };
}

export function websiteSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "ClaimHub UK",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en-GB",
    };
}
